import React, { useState, useEffect } from 'react';

const JSONBIN_ID = '69750825ae596e708ff2d523';
const JSONBIN_URL = `https://api.jsonbin.io/v3/b/${JSONBIN_ID}`;
const JSONBIN_KEY = '$2a$10$zWeDCcv/08dfJKpRyW6lv.HE2MG/Q9KitKQ6falrFUU.0z3UuDqCG';

export default function SalarisverwerkingDashboard({ onBack }) {
  const [activeStep, setActiveStep] = useState(null);
  const [showFlow, setShowFlow] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [showPrincipleModal, setShowPrincipleModal] = useState(false);
  const [editingPrinciple, setEditingPrinciple] = useState(null);
  const [showNextStepModal, setShowNextStepModal] = useState(false);
  const [editingNextStepIndex, setEditingNextStepIndex] = useState(null);
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.pushState(null, '', window.location.pathname);
      window.location.hash = '';
      window.location.reload();
    }
  };

  // Default data
  const defaultIdeaData = {
    id: 'IDEA-003',
    title: 'Salarisverwerking Vereenvoudigen',
    status: 'it-design',
    intakeDate: '2025-01-20',
    requester: 'Jelle',
    owner: 'Jelle',
    lastUpdated: new Date().toISOString()
  };

  const defaultPhases = [
    {
      id: 'step1', label: '1', size: 'M', title: 'Urenregistratie Import',
      items: ['Excel/CSV upload mogelijkheid', 'Automatische validatie', 'Foutmeldingen bij inconsistenties'],
      details: {
        description: 'Importeren van urenregistraties vanuit externe systemen.',
        tasks: ['Import interface bouwen', 'Validatie regels definiëren', 'Error handling implementeren'],
        risks: ['Verschillende Excel formaten'],
        owner: 'Dev'
      }
    },
    {
      id: 'step2', label: '2', size: 'L', title: 'Berekening Engine',
      items: ['Bruto-netto berekeningen', 'Toeslagen en inhoudingen', 'Belastingregels toepassen'],
      details: {
        description: 'Core engine voor alle salarisberekeningen.',
        tasks: ['Berekeningslogica bouwen', 'Belastingtabellen implementeren', 'Toeslagen configureerbaar maken', 'Unit tests schrijven'],
        risks: ['Complexe belastingwetgeving', 'Jaarlijkse wijzigingen'],
        owner: 'Dev + Finance'
      }
    },
    {
      id: 'step3', label: '3', size: 'M', title: 'Goedkeuringsflow',
      items: ['Manager review stap', 'Finance accordering', 'Audit trail'],
      details: {
        description: 'Workflow voor goedkeuring van salarisruns.',
        tasks: ['Workflow engine bouwen', 'Notificaties implementeren', 'Audit logging', 'Rol-gebaseerde toegang'],
        risks: ['Bottleneck bij goedkeurders'],
        owner: 'Product + Dev'
      }
    },
    {
      id: 'step4', label: '4', size: 'S', title: 'Export & Rapportage',
      items: ['Bankbestand genereren', 'Loonstroken PDF', 'Management rapportages'],
      details: {
        description: 'Output generatie voor bank en medewerkers.',
        tasks: ['SEPA bestand generatie', 'PDF template loonstrook', 'Dashboard met KPIs'],
        risks: ['Bank format wijzigingen'],
        owner: 'Dev'
      }
    }
  ];

  const defaultPrinciples = [
    { id: 'p1', icon: '🎯', title: 'Foutloos', desc: 'Zero tolerance voor rekenfouten' },
    { id: 'p2', icon: '⚡', title: 'Efficiënt', desc: 'Minimale handmatige handelingen' },
    { id: 'p3', icon: '🔒', title: 'Compliant', desc: 'Voldoen aan wet- en regelgeving' },
    { id: 'p4', icon: '📊', title: 'Transparant', desc: 'Volledige audit trail' }
  ];

  const defaultNextSteps = [
    'Huidige proces in kaart brengen met Jelle',
    'Belastingtabellen en regels verzamelen',
    'Prototype berekening engine bouwen',
    'Test cases definiëren met Finance'
  ];

  const [ideaData, setIdeaData] = useState(defaultIdeaData);
  const [phases, setPhases] = useState(defaultPhases);
  const [principles, setPrinciples] = useState(defaultPrinciples);
  const [nextSteps, setNextSteps] = useState(defaultNextSteps);

  const statusOptions = [
    { id: 'exploration', label: 'Exploration', desc: 'Idee vastleggen en verdiepen' },
    { id: 'it-design', label: 'IT-Design', desc: 'UX/technical design uitwerken' },
    { id: 'prioritization', label: 'Prioritization', desc: 'Impact/effort bepalen' },
    { id: 'execution', label: 'Execution', desc: 'Epics → Stories → Development' }
  ];

  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL'];
  const iconOptions = ['💡', '🎯', '📈', '🤝', '⚡', '🔒', '🚀', '✨', '🎨', '🔧', '📊', '💬', '👥', '🏆', '⭐'];

  const getStatusIndex = (status) => statusOptions.findIndex(s => s.id === status);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(JSONBIN_URL + '/latest', { headers: { 'X-Master-Key': JSONBIN_KEY } });
      if (response.ok) {
        const data = await response.json();
        if (data.record?.ideas?.salarisverwerking) {
          const saved = data.record.ideas.salarisverwerking;
          if (saved.ideaData) setIdeaData(saved.ideaData);
          if (saved.phases) setPhases(saved.phases);
          if (saved.principles) setPrinciples(saved.principles);
          if (saved.nextSteps) setNextSteps(saved.nextSteps);
        }
      }
    } catch (error) {
      console.log('Load failed:', error);
    }
    setIsLoading(false);
  };

  const saveAllData = async (newIdea, newPhases, newPrinciples, newNextSteps) => {
    setIsSaving(true);
    const dataToSave = {
      ideaData: newIdea || ideaData,
      phases: newPhases || phases,
      principles: newPrinciples || principles,
      nextSteps: newNextSteps || nextSteps,
      lastUpdated: new Date().toISOString()
    };
    
    if (newIdea) setIdeaData(newIdea);
    if (newPhases) setPhases(newPhases);
    if (newPrinciples) setPrinciples(newPrinciples);
    if (newNextSteps) setNextSteps(newNextSteps);
    
    try {
      const getRes = await fetch(JSONBIN_URL + '/latest', { headers: { 'X-Master-Key': JSONBIN_KEY } });
      const current = await getRes.json();
      const updated = { ...current.record, ideas: { ...current.record?.ideas, salarisverwerking: dataToSave } };
      await fetch(JSONBIN_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Master-Key': JSONBIN_KEY },
        body: JSON.stringify(updated)
      });
    } catch (error) {
      console.error('Save failed:', error);
    }
    setIsSaving(false);
  };

  const handleStatusChange = (newStatus) => {
    saveAllData({ ...ideaData, status: newStatus }, null, null, null);
    setShowStatusMenu(false);
  };

  // Phase CRUD
  const openPhaseModal = (phase = null) => {
    setEditingPhase(phase ? JSON.parse(JSON.stringify(phase)) : {
      id: 'step' + Date.now(), label: String(phases.length + 1), size: 'M', title: '',
      items: [''], details: { description: '', tasks: [''], risks: [''], owner: '' }
    });
    setShowPhaseModal(true);
  };

  const savePhase = (phaseData) => {
    const clean = { ...phaseData, items: phaseData.items.filter(i => i.trim()),
      details: { ...phaseData.details, tasks: phaseData.details.tasks.filter(t => t.trim()), risks: phaseData.details.risks.filter(r => r.trim()) }
    };
    const idx = phases.findIndex(p => p.id === clean.id);
    const newPhases = idx >= 0 ? phases.map(p => p.id === clean.id ? clean : p) : [...phases, clean];
    saveAllData(null, newPhases, null, null);
    setShowPhaseModal(false);
  };

  const deletePhase = (id) => {
    if (window.confirm('Stap verwijderen?')) {
      const newPhases = phases.filter(p => p.id !== id).map((p, i) => ({ ...p, label: String(i + 1) }));
      saveAllData(null, newPhases, null, null);
    }
  };

  const movePhase = (id, dir) => {
    const idx = phases.findIndex(p => p.id === id);
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= phases.length) return;
    const newPhases = [...phases];
    [newPhases[idx], newPhases[newIdx]] = [newPhases[newIdx], newPhases[idx]];
    saveAllData(null, newPhases.map((p, i) => ({ ...p, label: String(i + 1) })), null, null);
  };

  // Principle CRUD
  const openPrincipleModal = (p = null) => {
    setEditingPrinciple(p ? { ...p } : { id: 'p' + Date.now(), icon: '💡', title: '', desc: '' });
    setShowPrincipleModal(true);
  };

  const savePrinciple = (data) => {
    const idx = principles.findIndex(p => p.id === data.id);
    const newPrinciples = idx >= 0 ? principles.map(p => p.id === data.id ? data : p) : [...principles, data];
    saveAllData(null, null, newPrinciples, null);
    setShowPrincipleModal(false);
  };

  const deletePrinciple = (id) => {
    if (window.confirm('Uitgangspunt verwijderen?')) {
      saveAllData(null, null, principles.filter(p => p.id !== id), null);
    }
  };

  // NextStep CRUD
  const openNextStepModal = (idx = null) => {
    setEditingNextStepIndex(idx);
    setShowNextStepModal(true);
  };

  const saveNextStep = (text) => {
    const newSteps = editingNextStepIndex !== null 
      ? nextSteps.map((s, i) => i === editingNextStepIndex ? text : s) 
      : [...nextSteps, text];
    saveAllData(null, null, null, newSteps);
    setShowNextStepModal(false);
  };

  const deleteNextStep = (idx) => {
    if (window.confirm('Stap verwijderen?')) {
      saveAllData(null, null, null, nextSteps.filter((_, i) => i !== idx));
    }
  };

  // Edit Idea Modal
  const EditIdeaModal = () => {
    const [form, setForm] = useState(ideaData);
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-bold">✏️ Idee Details</h2>
            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
          <div className="space-y-3">
            <div><label className="block text-sm font-medium mb-1">ID</label>
              <input value={form.id} onChange={e => setForm({...form, id: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Titel</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Intake Datum</label>
              <input type="date" value={form.intakeDate} onChange={e => setForm({...form, intakeDate: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Aanvrager</label>
              <input value={form.requester} onChange={e => setForm({...form, requester: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Eigenaar</label>
              <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Annuleren</button>
            <button onClick={() => { saveAllData(form, null, null, null); setShowEditModal(false); }} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg">Opslaan</button>
          </div>
        </div>
      </div>
    );
  };

  // Phase Modal
  const PhaseModal = () => {
    const [form, setForm] = useState(editingPhase);
    const updateItem = (i, v) => { const items = [...form.items]; items[i] = v; setForm({...form, items}); };
    const updateTask = (i, v) => { const tasks = [...form.details.tasks]; tasks[i] = v; setForm({...form, details: {...form.details, tasks}}); };
    const updateRisk = (i, v) => { const risks = [...form.details.risks]; risks[i] = v; setForm({...form, details: {...form.details, risks}}); };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          <div className="flex justify-between p-4 border-b bg-green-50">
            <h2 className="text-lg font-bold">✏️ Stap Bewerken</h2>
            <button onClick={() => setShowPhaseModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Titel *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">T-shirt Size</label>
                <select value={form.size} onChange={e => setForm({...form, size: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Beschrijving</label>
              <textarea value={form.details.description} onChange={e => setForm({...form, details: {...form.details, description: e.target.value}})} rows={2} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-sm font-medium">Bullet Points</label>
                <button type="button" onClick={() => setForm({...form, items: [...form.items, '']})} className="text-xs text-green-500">+ Toevoegen</button></div>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={item} onChange={e => updateItem(i, e.target.value)} className="flex-1 px-3 py-2 border rounded-lg" />
                  <button onClick={() => setForm({...form, items: form.items.filter((_, idx) => idx !== i)})} className="px-2 text-red-400">×</button>
                </div>
              ))}
            </div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-sm font-medium">Taken</label>
                <button type="button" onClick={() => setForm({...form, details: {...form.details, tasks: [...form.details.tasks, '']}})} className="text-xs text-green-500">+ Toevoegen</button></div>
              {form.details.tasks.map((task, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={task} onChange={e => updateTask(i, e.target.value)} className="flex-1 px-3 py-2 border rounded-lg" />
                  <button onClick={() => setForm({...form, details: {...form.details, tasks: form.details.tasks.filter((_, idx) => idx !== i)}})} className="px-2 text-red-400">×</button>
                </div>
              ))}
            </div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-sm font-medium">Risico's</label>
                <button type="button" onClick={() => setForm({...form, details: {...form.details, risks: [...form.details.risks, '']}})} className="text-xs text-green-500">+ Toevoegen</button></div>
              {form.details.risks.map((risk, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={risk} onChange={e => updateRisk(i, e.target.value)} className="flex-1 px-3 py-2 border rounded-lg" />
                  <button onClick={() => setForm({...form, details: {...form.details, risks: form.details.risks.filter((_, idx) => idx !== i)}})} className="px-2 text-red-400">×</button>
                </div>
              ))}
            </div>
            <div><label className="block text-sm font-medium mb-1">Eigenaar</label>
              <input value={form.details.owner} onChange={e => setForm({...form, details: {...form.details, owner: e.target.value}})} className="w-full px-3 py-2 border rounded-lg" /></div>
          </div>
          <div className="flex gap-3 p-4 border-t">
            <button onClick={() => setShowPhaseModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Annuleren</button>
            <button onClick={() => savePhase(form)} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg font-medium">💾 Opslaan</button>
          </div>
        </div>
      </div>
    );
  };

  // Principle Modal
  const PrincipleModal = () => {
    const [form, setForm] = useState(editingPrinciple);
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-bold">✏️ Uitgangspunt</h2>
            <button onClick={() => setShowPrincipleModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium mb-2">Icoon</label>
              <div className="flex flex-wrap gap-2">
                {iconOptions.map(icon => (
                  <button key={icon} type="button" onClick={() => setForm({...form, icon})}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${form.icon === icon ? 'bg-green-100 ring-2 ring-green-500' : 'bg-gray-100'}`}>{icon}</button>
                ))}
              </div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Titel *</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-medium mb-1">Beschrijving</label>
              <input value={form.desc} onChange={e => setForm({...form, desc: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowPrincipleModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Annuleren</button>
            <button onClick={() => savePrinciple(form)} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg">Opslaan</button>
          </div>
        </div>
      </div>
    );
  };

  // NextStep Modal
  const NextStepModal = () => {
    const [text, setText] = useState(editingNextStepIndex !== null ? nextSteps[editingNextStepIndex] : '');
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-bold">✏️ Vervolgstap</h2>
            <button onClick={() => setShowNextStepModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
          <div><label className="block text-sm font-medium mb-1">Tekst *</label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={3} className="w-full px-3 py-2 border rounded-lg" /></div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowNextStepModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Annuleren</button>
            <button onClick={() => saveNextStep(text)} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg">Opslaan</button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Laden...</p>
        </div>
      </div>
    );
  }

  const sizeColors = { XS: '#9CA3AF', S: '#60A5FA', M: '#F59E0B', L: '#EF4444', XL: '#7C3AED' };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Back Button */}
        <button onClick={handleBack} className="flex items-center gap-2 mb-4 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow transition text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          <span className="font-medium">Terug naar Overzicht</span>
        </button>
        
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-green-500 to-green-400 rounded-xl shadow-sm p-4 mb-4 text-white">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-green-200 text-sm">Idee</span>
                <span className="font-bold text-lg">{ideaData.id}</span>
              </div>
              <div className="hidden sm:block h-8 w-px bg-green-300"></div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span><span className="text-green-200">Intake:</span> {new Date(ideaData.intakeDate).toLocaleDateString('nl-NL')}</span>
                <span><span className="text-green-200">Aanvrager:</span> {ideaData.requester}</span>
                <button onClick={() => setShowEditModal(true)} className="p-1 hover:bg-white/20 rounded" title="Bewerken">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                </button>
                {isSaving && <span className="text-xs animate-pulse">Opslaan...</span>}
              </div>
            </div>
            
            {/* Status */}
            <div className="relative">
              <button onClick={() => setShowStatusMenu(!showStatusMenu)} className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5">
                {statusOptions.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-1 text-xs">
                    <div className={`w-2 h-2 rounded-full ${i <= getStatusIndex(ideaData.status) ? 'bg-white' : 'bg-green-300'}`}></div>
                    <span className={`hidden sm:inline ${i === getStatusIndex(ideaData.status) ? 'font-medium' : 'text-green-200'}`}>{s.label}</span>
                    {i < statusOptions.length - 1 && <span className="hidden sm:inline text-green-300 mx-1">→</span>}
                  </div>
                ))}
              </button>
              {showStatusMenu && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border py-2 min-w-64 z-50">
                  {statusOptions.map((s, i) => (
                    <button key={s.id} onClick={() => handleStatusChange(s.id)}
                      className={`w-full px-4 py-2 text-left hover:bg-green-50 flex items-center gap-3 ${ideaData.status === s.id ? 'bg-green-50' : ''}`}>
                      <div className={`w-3 h-3 rounded-full ${i <= getStatusIndex(ideaData.status) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div><div className="font-medium text-gray-800">{s.label}</div><div className="text-xs text-gray-500">{s.desc}</div></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center text-3xl">💶</div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{ideaData.title}</h1>
                <p className="text-gray-500">Eigenaar: {ideaData.owner}</p>
              </div>
            </div>
            <button onClick={() => setShowFlow(!showFlow)} className={`px-4 py-2 rounded-lg font-medium ${showFlow ? 'bg-green-500 text-white' : 'bg-green-100 text-green-600'}`}>
              {showFlow ? '📋 Kaarten' : '🔀 Flowchart'}
            </button>
          </div>
        </div>

        {/* Principles */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">📐 Uitgangspunten</h2>
            <button onClick={() => openPrincipleModal()} className="text-sm px-3 py-1 rounded-lg border-2 border-dashed border-green-300 text-green-500 hover:bg-green-50">+ Toevoegen</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {principles.map(p => (
              <div key={p.id} className="bg-gray-50 rounded-lg p-4 relative group">
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1">
                  <button onClick={() => openPrincipleModal(p)} className="p-1 text-gray-400 hover:text-green-500 bg-white rounded shadow-sm text-xs">✏️</button>
                  <button onClick={() => deletePrinciple(p.id)} className="p-1 text-gray-400 hover:text-red-500 bg-white rounded shadow-sm text-xs">🗑️</button>
                </div>
                <span className="text-2xl">{p.icon}</span>
                <h3 className="font-semibold text-gray-800 mt-2">{p.title}</h3>
                <p className="text-sm text-gray-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Phases */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">{showFlow ? '🔀 Process Flow' : '📋 Epic Stappen'}</h2>
            <button onClick={() => openPhaseModal()} className="text-sm px-3 py-1 rounded-lg border-2 border-dashed border-green-300 text-green-500 hover:bg-green-50">+ Stap Toevoegen</button>
          </div>
          
          {showFlow ? (
            <div className="bg-white rounded-xl shadow-sm p-6 overflow-x-auto">
              <svg viewBox={`0 0 ${Math.max(900, phases.length * 210)} 180`} className="w-full min-w-[800px]">
                {phases.map((phase, idx) => {
                  const x = 50 + idx * 210;
                  return (
                    <g key={phase.id}>
                      <rect x={x} y="20" width="180" height="130" rx="12" fill="white" stroke="#E5E7EB" strokeWidth="2"/>
                      <rect x={x} y="20" width="180" height="40" rx="12" fill="#F0FDF4"/>
                      <rect x={x} y="48" width="180" height="12" fill="#F0FDF4"/>
                      <circle cx={x + 20} cy="40" r="14" fill="#22C55E"/>
                      <text x={x + 20} y="45" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">{phase.label}</text>
                      <text x={x + 45} y="45" fill="#1F2937" fontSize="13" fontWeight="600">{phase.title.substring(0, 18)}</text>
                      <rect x={x + 150} y="28" width="24" height="18" rx="4" fill={sizeColors[phase.size]}/>
                      <text x={x + 162} y="41" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">{phase.size}</text>
                      {phase.items.slice(0, 3).map((item, i) => (
                        <text key={i} x={x + 12} y={75 + i * 18} fill="#6B7280" fontSize="11">• {item.length > 26 ? item.substring(0, 26) + '...' : item}</text>
                      ))}
                      {idx < phases.length - 1 && <path d={`M${x + 180} 85 L${x + 210} 85`} stroke="#22C55E" strokeWidth="2" markerEnd="url(#arrowGreen)"/>}
                    </g>
                  );
                })}
                <defs><marker id="arrowGreen" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#22C55E"/></marker></defs>
              </svg>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {phases.map((phase, idx) => (
                <div key={phase.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${activeStep === phase.id ? 'ring-2 ring-green-500' : ''}`}>
                  <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => setActiveStep(activeStep === phase.id ? null : phase.id)}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">{phase.label}</div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{phase.title}</h3>
                          <span className="inline-block px-2 py-0.5 rounded text-xs text-white" style={{backgroundColor: sizeColors[phase.size]}}>{phase.size}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {idx > 0 && <button onClick={e => {e.stopPropagation(); movePhase(phase.id, 'up');}} className="p-1 text-gray-400 hover:text-green-500 text-xs">▲</button>}
                        {idx < phases.length - 1 && <button onClick={e => {e.stopPropagation(); movePhase(phase.id, 'down');}} className="p-1 text-gray-400 hover:text-green-500 text-xs">▼</button>}
                        <button onClick={e => {e.stopPropagation(); openPhaseModal(phase);}} className="p-2 text-gray-400 hover:text-green-500">✏️</button>
                        <button onClick={e => {e.stopPropagation(); deletePhase(phase.id);}} className="p-2 text-gray-400 hover:text-red-500">🗑️</button>
                        <svg className={`w-5 h-5 text-gray-400 transition-transform ${activeStep === phase.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                      </div>
                    </div>
                    <ul className="mt-3 space-y-1">
                      {phase.items.map((item, i) => <li key={i} className="text-sm text-gray-600">• {item}</li>)}
                    </ul>
                  </div>
                  {activeStep === phase.id && (
                    <div className="border-t bg-gray-50 p-4">
                      <p className="text-gray-600 mb-4">{phase.details.description}</p>
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Taken:</h4>
                        <ul className="space-y-1">{phase.details.tasks.map((t, i) => <li key={i} className="text-sm text-gray-600">✓ {t}</li>)}</ul>
                      </div>
                      {phase.details.risks.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-700 mb-2">Risico's:</h4>
                          <ul className="space-y-1">{phase.details.risks.map((r, i) => <li key={i} className="text-sm text-amber-600">⚠️ {r}</li>)}</ul>
                        </div>
                      )}
                      <div className="text-sm text-gray-500">👤 Eigenaar: <span className="font-medium text-gray-700">{phase.details.owner}</span></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">⏭️ Directe Vervolgstappen</h2>
            <button onClick={() => openNextStepModal()} className="text-sm px-3 py-1 rounded-lg border-2 border-dashed border-green-300 text-green-500 hover:bg-green-50">+ Toevoegen</button>
          </div>
          <div className="space-y-2">
            {nextSteps.map((step, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg group">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                  <span className="text-gray-700">{step}</span>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                  <button onClick={() => openNextStepModal(idx)} className="p-1 text-gray-400 hover:text-green-500">✏️</button>
                  <button onClick={() => deleteNextStep(idx)} className="p-1 text-gray-400 hover:text-red-500">🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">Laatst bijgewerkt: {new Date(ideaData.lastUpdated || Date.now()).toLocaleString('nl-NL')}</p>
      </div>

      {/* Modals */}
      {showEditModal && <EditIdeaModal />}
      {showPhaseModal && editingPhase && <PhaseModal />}
      {showPrincipleModal && editingPrinciple && <PrincipleModal />}
      {showNextStepModal && <NextStepModal />}
    </div>
  );
}
